<script setup lang="ts">
  import { X } from 'lucide-vue-next';
  import CheckboxCheckIcon from '@/shared/assets/icons/CheckboxCheckIcon.svg';
  import { Accordion, AccordionContent, AccordionItem, ColoredButton } from '@/shared/ui';
  import { computed, ref } from 'vue';
  import PermissionsAccordionTrigger from './PermissionsAccordionTrigger.vue';
  import EraserIcon from '@/shared/assets/icons/EraserIcon.svg';

  import type { PermissionsSelectedItem } from '../../model/form-permissions-selection.store';

  const props = withDefaults(
    defineProps<{ fields?: PermissionsSelectedItem[]; locked?: boolean; readonly?: boolean }>(),
    {
      fields: () => [],
      locked: false,
      readonly: false,
    }
  );

  const emit = defineEmits<{
    (e: 'remove', fieldId: string): void;
    (e: 'clear'): void;
  }>();

  const openSections = ref(['selected-fields']);

  const counter = computed(() =>
    props.fields.length === 0 ? '0' : String(props.fields.length).padStart(2, '0')
  );

  function describeItem(item: PermissionsSelectedItem, index: number): string {
    if (item.kind === 'table') return `${index + 1}º. ID: ${item.displayId}`;
    return `${index + 1}º. ${item.label} — ID: ${item.displayId}`;
  }
</script>

<template>
  <Accordion v-model="openSections" type="multiple">
    <AccordionItem
      value="selected-fields"
      class="rounded-lg border border-gray-02 p-4 last:border-b"
      :disabled="locked"
      data-testid="permissions-selected-fields-section"
    >
      <PermissionsAccordionTrigger :icon="CheckboxCheckIcon">
        Campos selecionados
      </PermissionsAccordionTrigger>

      <AccordionContent class="pt-4 pb-0">
        <div class="flex flex-col gap-3">
          <ul
            v-if="fields.length > 0"
            class="flex max-h-[232px] flex-col gap-2 overflow-y-auto [scrollbar-color:var(--color-gray-03)_transparent] [scrollbar-width:thin]"
            data-testid="permissions-selected-fields-list"
          >
            <li
              v-for="(field, index) in fields"
              :key="`${field.kind}-${field.id}`"
              class="flex items-center justify-between gap-2 rounded-lg bg-gray-02 px-3 py-2 text-sm text-gray-07"
              data-testid="permissions-selected-field-item"
            >
              <span class="min-w-0 truncate">{{ describeItem(field, index) }}</span>
              <button
                type="button"
                class="shrink-0 text-gray-05 disabled:cursor-not-allowed disabled:opacity-50"
                :aria-label="`Remover ${field.displayId}`"
                :disabled="readonly"
                @click="emit('remove', field.id)"
              >
                <X class="size-4" aria-hidden="true" />
              </button>
            </li>
          </ul>

          <p class="text-sm text-gray-05" data-testid="permissions-selected-fields-count">
            Total de campos selecionados: {{ counter }}
          </p>

          <ColoredButton
            class="!h-[42px] w-full justify-center"
            text="Limpar selecionados"
            :icon="EraserIcon"
            icon-alt="Limpar selecionados"
            outlined
            :disabled="readonly || fields.length === 0"
            data-testid="permissions-clear-selected-btn"
            @click="emit('clear')"
          />
        </div>
      </AccordionContent>
    </AccordionItem>
  </Accordion>
</template>
